import { ChevronDownIcon, HomeIcon, UserCircleIcon, ArrowLeftOnRectangleIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import { getDashboardLabel } from "../hooks/useNavigation";

const menuVariants = {
  initial: { opacity: 0, y: -10, scale: 0.95 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit:    { opacity: 0, y: -10, scale: 0.95 },
};

interface Props {
  profile: any;
  avatarUrl: string;
  userDisplayName: string;
  isOpen: boolean;
  isSigningOut: boolean;
  isRTL: boolean;
  buttonRef: React.RefObject<HTMLButtonElement>;
  panelRef: React.RefObject<HTMLDivElement>;
  onToggle: () => void;
  onDashboard: () => void;
  onProfile: () => void;
  onSignOut: () => void;
  t: (key: string) => string;
}

export default function NavUserMenu({
  profile, avatarUrl, userDisplayName, isOpen, isSigningOut, isRTL,
  buttonRef, panelRef, onToggle, onDashboard, onProfile, onSignOut, t,
}: Props) {
  const menuItem = "flex w-full items-center gap-3 px-3 py-2.5 rounded-lg transition-colors";

  return (
    <div className="relative" ref={panelRef}>
      <motion.button ref={buttonRef} onClick={onToggle}
        className="flex items-center gap-2.5 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors group"
        whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
        aria-label="User menu" aria-expanded={isOpen} aria-haspopup="true">
        <img src={avatarUrl} alt={userDisplayName}
          className="w-8 h-8 rounded-full object-cover border-2 border-[#b2d12e]/20 group-hover:border-[#b2d12e]/50 transition-colors" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userDisplayName)}&background=b2d12e&color=fff&bold=true&size=32`; }} />
        <div className="hidden lg:flex flex-col items-start">
          <span className="text-xs text-gray-500">{profile?.email}</span>
          <p className="text-sm font-medium text-gray-900 truncate max-w-[150px]">{userDisplayName}</p>
        </div>
        <ChevronDownIcon className={`hidden lg:block w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div variants={menuVariants} initial="initial" animate="animate" exit="exit"
            transition={{ duration: 0.2 }}
            className="absolute end-0 mt-2 w-80 bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden z-50"
            role="menu">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#b2d12e]/10 to-white border-b border-gray-200 p-4">
              <div className="flex items-center gap-3">
                <img src={avatarUrl} alt={userDisplayName}
                  className="w-12 h-12 rounded-lg object-cover border-2 border-[#b2d12e]" loading="lazy" onError={(e) => { (e.target as HTMLImageElement).src = `https://ui-avatars.com/api/?name=${encodeURIComponent(userDisplayName)}&background=b2d12e&color=fff&bold=true&size=32`; }} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 truncate">{userDisplayName}</p>
                  <p className="text-xs text-gray-500 truncate">{profile?.email}</p>
                  {profile?.role && (
                    <span className="inline-block text-xs font-medium text-[#b2d12e] mt-1 capitalize">{profile.role}</span>
                  )}
                </div>
              </div>
            </div>

            <div className="p-2">
              <motion.button whileHover={{ x: isRTL ? -4 : 4 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}
                onClick={onDashboard} className={`${menuItem} hover:bg-[#b2d12e]/10`} role="menuitem">
                <HomeIcon className="w-4 h-4 text-[#b2d12e]" />
                <span className="text-sm font-medium text-gray-700">{getDashboardLabel(profile?.role, t)}</span>
              </motion.button>

              <motion.button whileHover={{ x: isRTL ? -4 : 4 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}
                onClick={onProfile} className={`${menuItem} hover:bg-[#b2d12e]/10 mt-1`} role="menuitem">
                <UserCircleIcon className="w-4 h-4 text-[#b2d12e]" />
                <span className="text-sm font-medium text-gray-700">{t("nav.profile")}</span>
              </motion.button>

              <motion.button whileHover={{ x: isRTL ? -4 : 4 }} transition={{ type: "spring", stiffness: 400, damping: 10 }}
                onClick={onSignOut} disabled={isSigningOut}
                className={`${menuItem} hover:bg-red-50 mt-1 disabled:opacity-50 disabled:cursor-not-allowed`} role="menuitem">
                <ArrowLeftOnRectangleIcon className="w-4 h-4 text-red-500" />
                <span className="text-sm font-medium text-gray-700">
                  {isSigningOut ? t("common.signingOut") : t("nav.signOut")}
                </span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
